import React from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import SectionTitle from "../layout/SectionTitle";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';

const getMetiers = (t) => [
  {
    title: t('home.metiers.btp.title'),
    description: t('home.metiers.btp.description'),
    image: "/images/btp.png",
    link: "/btp",
  },
  {
    title: t('home.metiers.immobilier.title'),
    description: t('home.metiers.immobilier.description'),
    image: "/images/projects/lac2.jpg",
    link: "/promotion-immobiliere",
  },
  {
    title: t('home.metiers.mines.title'),
    description: t('home.metiers.mines.description'),
    image: "/images/mines.png",
    link: "/mines",
  },
];

function Arrow({ direction, onClick }) {
  return (
    <button
      className={`absolute top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full w-10 h-10 hidden md:flex md:items-center md:justify-center shadow-md ${
        direction === "left" ? "left-6" : "right-6"
      }`}
      onClick={onClick}
    >
      {direction === "left" ? <FaChevronLeft size={18} /> : <FaChevronRight size={18} />}
    </button>
  );
}

export default function MetiersSection() {
  const { t } = useTranslation();
  const metiers = getMetiers(t);
  const [sectionRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    pauseOnHover: true,
    nextArrow: <Arrow direction="right" />,
    prevArrow: <Arrow direction="left" />,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    draggable: true,
    dotsClass: "slick-dots custom-dots",
    appendDots: dots => (
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30">
        <ul>{dots}</ul>
      </div>
    )
  };

  return (
    <section className="py-8 bg-white" ref={sectionRef}>
      <motion.div 
        className="container mx-auto px-4"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8 }}
      >
        <SectionTitle
          overline={t('home.metiers.title')}
          highlight={t('home.metiers.highlight')}
          showLogo={true}
          align="left"
        />
      </motion.div>

      <motion.div 
        className="container mx-auto px-4 relative overflow-hidden rounded-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <Slider {...settings}>
          {metiers.map((metier, index) => (
            <div key={index}>
              <div className="relative h-[400px] md:h-[500px] lg:h-[571px]">
                <img
                  src={metier.image}
                  alt={metier.title}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 z-10" />
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 text-white text-center">
                  <motion.h3 
                    className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-md"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    {metier.title}
                  </motion.h3>
                  <motion.p 
                    className="text-base md:text-xl max-w-3xl mb-8 drop-shadow-sm px-4 mx-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    {metier.description.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <Link
                      to={metier.link}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="inline-block px-6 py-3 bg-socabeg-blue text-white font-semibold rounded-md hover:bg-opacity-90 transition-colors duration-300 shadow-md text-base"
                    >
                    {t('home.metiers.discover')}
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </motion.div>

      <style>{`
        .custom-dots {
          display: flex !important;
          justify-content: center !important;
          gap: 30px !important;
          position: absolute !important;
          bottom: 8px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          z-index: 30 !important;
          width: auto !important;
        }
        .custom-dots li {
          margin: 0 !important;
          width: auto !important;
          height: auto !important;
        }
        .custom-dots li button {
          width: 12px !important;
          height: 12px !important;
          border-radius: 50% !important;
          background-color: rgba(255, 255, 255, 0.7) !important;
          font-size: 0 !important;
          border: none !important;
          padding: 0 !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3) !important;
        }
        .custom-dots li button:hover {
          transform: scale(1.2) !important;
          background-color: rgba(255, 255, 255, 1) !important;
        }
        .custom-dots li.slick-active button {
          background-color: #E30613 !important;
        }
        .custom-dots li button:before {
          content: '' !important;
          display: none !important;
        }
      `}</style>
    </section>
  );
}
